import React, { createContext, useState, useEffect } from 'react';

export const ConfigContext = createContext();

const PALETTE_TO_CSS = {
  brandPrimary:   '--brand-primary',
  brandSecondary: '--brand-secondary',
  brandAccent:    '--brand-accent',
  brandLight:     '--brand-light',
  bgPrimary:      '--bg-primary',
  bgSecondary:    '--bg-secondary',
  bgDark:         '--bg-dark',
  textPrimary:    '--text-primary',
  textSecondary:  '--text-secondary',
  accentSuccess:  '--accent-success',
  accentHover:    '--accent-hover',
  borderLight:    '--border-light'
};

const DEFAULT_PALETTE = {
  brandPrimary: '#1a1a1a',
  brandSecondary: '#c9a96e',
  brandAccent: '#8b7355',
  brandLight: '#f5f0eb',
  bgPrimary: '#faf9f7',
  bgSecondary: '#f5f0eb',
  bgDark: '#1a1a1a',
  textPrimary: '#1a1a1a',
  textSecondary: '#6b6560',
  accentSuccess: '#1a1a1a',
  accentHover: '#c9a96e',
  borderLight: '#e8e2da'
};

const DEFAULT_BRAND = {
  name: 'Rem Urban Clothes',
  logo: ''
};

const DEFAULT_TYPOGRAPHY = {
  heading: 'Cormorant Garamond',
  body: 'Inter',
  accent: 'Playfair Display'
};

export const applyPalette = (palette) => {
  const root = document.documentElement;
  const merged = { ...DEFAULT_PALETTE, ...palette };
  for (const [key, cssVar] of Object.entries(PALETTE_TO_CSS)) {
    root.style.setProperty(cssVar, merged[key]);
  }
};

export const applyTypography = (typography) => {
  const root = document.documentElement;
  const merged = { ...DEFAULT_TYPOGRAPHY, ...typography };

  root.style.setProperty('--font-heading', `'${merged.heading}', serif`);
  root.style.setProperty('--font-body', `'${merged.body}', sans-serif`);
  root.style.setProperty('--font-accent', `'${merged.accent}', serif`);

  const fontFamilies = [merged.heading, merged.body, merged.accent]
    .filter((value, index, self) => self.indexOf(value) === index) // Unique fonts
    .map(f => f.replace(/ /g, '+'))
    .join('&family=');

  const fontUrl = `https://fonts.googleapis.com/css2?family=${fontFamilies}&display=swap`;
  
  let link = document.getElementById('dynamic-google-fonts');
  if (!link) {
    link = document.createElement('link');
    link.id = 'dynamic-google-fonts';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  link.href = fontUrl;
};

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState({
    colorPalette: DEFAULT_PALETTE,
    typography: DEFAULT_TYPOGRAPHY,
    brand: DEFAULT_BRAND,
    socialLinks: []
  });
  const [loading, setLoading] = useState(true);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/home-content/config`);
      if (!res.ok) throw new Error('Network response was not ok');
      const json = await res.json();
      
      const newConfig = {
        colorPalette: json.data?.colorPalette || DEFAULT_PALETTE,
        typography: json.data?.typography || DEFAULT_TYPOGRAPHY,
        brand: json.data?.brand || DEFAULT_BRAND,
        socialLinks: json.data?.socialLinks || []
      };

      setConfig(newConfig);
      applyPalette(newConfig.colorPalette);
      applyTypography(newConfig.typography);
    } catch (error) {
      console.error('Error fetching global config:', error);
      applyPalette(DEFAULT_PALETTE);
      applyTypography(DEFAULT_TYPOGRAPHY);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  return (
    <ConfigContext.Provider value={{ config, loading, refreshConfig: fetchConfig }}>
      {children}
    </ConfigContext.Provider>
  );
};
